<script setup lang="ts">

import DestinationsHero from "@/pages/Destinations/DestinationsHero.vue";
import {nextTick, onMounted, reactive, ref} from "vue";
import {useRouter} from "vue-router";
import CustomButton from "@/components/Button/Button.vue";
import ArticleCard from "@/components/ArticleCard/ArticleCard.vue";
import {useDestinationStore} from "@/stores/destinationStore";
import {useArticleStore} from "@/stores/articleStore";
import {useBlogStore} from "@/stores/blogStore";

const destinationStore = useDestinationStore();
const articleStore = useArticleStore();
const blogStore = useBlogStore();

const router = useRouter();
const slug = "travel-inspiration";

const isHovering = ref({}) // Object to track hover per item
const hoverHeights = reactive({});

function setHoverRef(el, index) {
  if (!el) return;
  nextTick(() => {
    hoverHeights[index] = el.offsetHeight;
  });
}

onMounted(() => {
  destinationStore.getAllDestinations();
  blogStore.getAllArticles(slug, blogStore.currentPage, blogStore.articlesPerPage);
});

</script>

<template>
  <DestinationsHero/>
  <div class="container mx-auto pt-32">

    <!-- Desktop -->
    <div class="hidden md:flex flex-col gap-14 place-content-center">
      <div
          v-for="(destinationItem, index) in destinationStore.allDestinations"
          class="relative w-full group overflow-hidden cursor-pointer"
          @mouseover="() => { isHovering[index] = true; nextTick(() => setHoverRef($refs[`hoverBox${index}`], index)); }"
          @mouseleave="isHovering[index] = false"
      >
        <img
            :src="destinationItem.image"
            alt="Destination"
            class="w-full h-[400px] sm:h-[600px] object-cover duration-700 group-hover:scale-105"
            @click="destinationStore.viewDestination(destinationItem.slug, '')"
        />

        <div>
          <div
              :style="{
                bottom: isHovering[index] ? `${hoverHeights[index] || 0}px` : '0px'
              }"
              :class="[
            'absolute z-20 bg-background-light py-10 px-10 w-[600px]  border-b border-primary-violet  group-hover:duration-700',
              index % 2 === 1 ? 'right-28' : 'left-28',
              isHovering[index] ? 'bottom-[150px]' : 'bottom-0'
              ]"
              @click="destinationStore.viewDestination(destinationItem.slug, '')"
          >
            <div class="absolute -top-6 left-25 transform -translate-x-1/2 z-30 bg-primary-violet text-white p-3 text-nowrap">
              {{ destinationItem.destinations.length }} destinations
            </div>
            <span
                class="responsive-title-1 text-primary-darkBlue font-thin"

            >
              {{ destinationItem.generalLocation }}
            </span>
          </div>


          <div v-show="isHovering[index]" :ref="el => setHoverRef(el, index)" :class="[
            'absolute bottom-0  z-20 bg-background-light py-8 px-10 w-[600px] border-b border-t border-primary-violet text-bw-midGrey ',
              index % 2 === 1 ? 'right-28' : 'left-28'
          ]"
          >
            <div class=" flex flex-col gap-4">
              <div class="flex items-center gap-1">
                <img src="@/assets/icons/address.svg" alt="Address" class="h-5 min-w-5"/>
                <div class="flex items-center gap-2 responsive-title-6">
                  DESTINATIONS
                </div>
              </div>

              <div class="flex flex-wrap gap-3 responsive-body-large">
                <div
                    v-for="(dest, index) in destinationItem.destinations.slice(0, 4)"
                    class="border-2 border-bw-midGrey p-3 rounded-md hover:!border-none hover:!bg-primary-violet hover:text-white"
                    @click="destinationStore.viewDestination(destinationItem.slug, dest.name)"
                >
                  {{ dest.name }}
                </div>
                <!-- Show "+X more" -->
                <div
                    v-if="destinationItem.destinations.length > 4"
                    class="border-2 border-bw-midGrey p-3 rounded-md bg-bw-lightGrey text-bw-darkGrey text-nowrap"
                    @click="destinationStore.viewDestination(destinationItem.slug, '')"
                >
                  +{{ destinationItem.destinations.length - 4 }}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>


    <!-- Mobile -->
    <div class="md:hidden flex flex-col gap-8">
      <div
          v-for="(destinationItem, index) in destinationStore.allDestinations"
          :key="index"
          class="flex flex-col min-w-[220px] cursor-pointer"
      >
        <!-- Image -->
        <div class="relative h-60 overflow-hidden group cursor-pointer">
          <img
              :src="destinationItem.image"
              alt="Destination"
              class="w-full h-full object-cover duration-500 group-hover:scale-105"
              @click="destinationStore.viewDestination(destinationItem.slug, '')"
          />
        </div>


        <div class="w-full bg-background-beige p-4">
          <div class="flex items-center"
               @click="destinationStore.viewDestination(destinationItem.slug, '')"
          >
            <div class="text-primary-darkBlue responsive-title-2 font-thin">
              {{ destinationItem.generalLocation }}
            </div>
          </div>

          <div class=" flex flex-col gap-2 mt-4 text-primary-darkBlue">
            <div class="flex items-center gap-1">
              <img src="@/assets/icons/address.svg" alt="Address" class="h-4 min-w-4"/>
              <div class="flex items-center gap-2 responsive-body-medium">
                DESTINATIONS
              </div>
            </div>

            <div class="flex flex-wrap gap-3 responsive-body-medium">
              <div
                  v-for="dest in destinationItem.destinations.slice(0, 4)"
                  :key="index"
                  class="border-[1px] border-bw-midGrey p-1 rounded-md hover:!border-none hover:!bg-primary-violet hover:text-white whitespace-nowrap"
                  @click="destinationStore.viewDestination(destinationItem.slug, dest.name)"
              >

                {{ dest.name }}
              </div>
              <!-- Show "+X more" -->
              <div
                  v-if="destinationItem.destinations.length > 4"
                  class="border-[1px] border-bw-midGrey p-1 rounded-md bg-bw-lightGrey text-bw-darkGrey text-nowrap"
                  @click="destinationStore.viewDestination(destinationItem.slug, '')"

              >
                +{{ destinationItem.destinations.length - 4 }} more
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <div class="mt-32 text-center bg-white py-24">
      <div class="container">
        <div class="responsive-title-1 text-center mb-4 text-nowrap text-primary-darkBlue font-thin">Get Inspired</div>
        <div class="text-bw-midGrey responsive-body-xlarge font-bold text-center max-w-3xl mx-auto">Want to discover the world, but dont know where to start? Take a look on our articles, and get your inspiration to plan your next trip.</div>
        <div class="flex flex-col gap-17 place-content-center items-center pt-24 overflow-x-hidden">
          <div class="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            <div v-for="(page, index) in blogStore.allArticles.slice(0, 3)" :key="index">
              <ArticleCard
                  :page="page"
                  @on-visit-article="articleStore.visitArticle(page.slug)"
              />
            </div>
          </div>
          <CustomButton
              type="gradient"
              :outlined="true"
              size="medium"
              :shadow="false"
              class="py-2 px-4"
              @click="router.push('/magazine')"
          >
            READ MORE ARTICLES
          </CustomButton>
        </div>
      </div>
    </div>



</template>

<style scoped>

</style>
